import { useCallback } from "react";
import { Editor } from "@tiptap/react";
import { toast } from "sonner";
import {
  sanitizeLinkHref,
  setEditorLink,
  removeEditorLink,
  getActiveLinkHref,
} from "../link";

/**
 * Hook for managing text formatting operations
 * Provides memoized callbacks for all format-related editor commands
 */
export function useEditorFormats(editor: Editor | null) {
  const setAlignment = useCallback(
    (alignment: string) => {
      editor?.chain().focus().setTextAlign(alignment).run();
    },
    [editor]
  );

  const toggleFormat = useCallback(
    (format: string) => {
      if (!editor) return;
      const chain = editor.chain().focus();

      switch (format) {
        case "bold":
          chain.toggleBold().run();
          break;
        case "italic":
          chain.toggleItalic().run();
          break;
        case "underline":
          chain.toggleUnderline().run();
          break;
        case "highlight":
          chain.toggleHighlight().run();
          break;
        case "bulletList":
          chain.toggleBulletList().run();
          break;
        case "orderedList":
          chain.toggleOrderedList().run();
          break;
        case "hr":
          chain.setHorizontalRule().run();
          break;
        case "table":
          chain.insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run();
          break;
        case "undo":
          chain.undo().run();
          break;
        case "redo":
          chain.redo().run();
          break;
      }
    },
    [editor]
  );

  const toggleLink = useCallback(() => {
    if (!editor) return;
    const isActive = editor.isActive("link");
    const previousUrl = getActiveLinkHref(editor);

    // On an existing link the prompt is prefilled with its URL so the user can
    // edit it, or clear it to remove the link entirely. On a fresh selection it
    // adds a link. Cancelling (null) leaves the document untouched.
    const url = window.prompt(
      isActive ? "Edit URL (clear the field to remove the link)" : "Enter URL",
      previousUrl || "https://"
    );
    if (url === null) return;

    if (url.trim() === "") {
      removeEditorLink(editor);
      return;
    }

    const href = sanitizeLinkHref(url);
    if (!href) {
      toast.error("That link uses an unsupported or unsafe URL.");
      return;
    }
    setEditorLink(editor, href);
  }, [editor]);

  return {
    setAlignment,
    toggleFormat,
    toggleLink,
  };
}
